import { inchesToParts, partsToInches } from "@/shared/utils/length";
import { Box, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface Props {
	value: number | null | undefined;
	onChange?: (value: number | null) => void;
	disabled?: boolean;
	label?: string;
}

export const FeetInchesInput = ({
	value,
	onChange = () => {},
	disabled = false,
	label,
}: Props) => {
	const feetRef = useRef<HTMLInputElement>(null);
	const inchesRef = useRef<HTMLInputElement>(null);

	const [feetDisplay, setFeetDisplay] = useState("");
	const [inchesDisplay, setInchesDisplay] = useState("");
	const isEditingRef = useRef(false);

	const handleFeet = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, "");
		if (raw.length > 3) return;
		isEditingRef.current = true;
		setFeetDisplay(raw);
		onChange(partsToInches(raw, inchesDisplay));
	};

	const handleInches = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, "");
		if (Number(raw) > 11) return;
		isEditingRef.current = true;
		setInchesDisplay(raw);
		onChange(partsToInches(feetDisplay, raw));
	};

	const normalize = () => {
		const total = partsToInches(feetDisplay, inchesDisplay);
		if (total === null) {
			setFeetDisplay("");
			setInchesDisplay("");
			return;
		}
		const parts = inchesToParts(total);
		setFeetDisplay(parts.feet);
		setInchesDisplay(parts.inches);
	};

	const onSegmentBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const related = e.relatedTarget as HTMLElement | null;
		if (related === feetRef.current || related === inchesRef.current) return;
		isEditingRef.current = false;
		normalize();
	};

	const onFeetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "'" || e.key === " " || (e.key === "Tab" && !e.shiftKey)) {
			e.preventDefault();
			inchesRef.current?.focus();
		}
	};

	useEffect(() => {
		if (isEditingRef.current) return;
		const parts = inchesToParts(value);
		setFeetDisplay(parts.feet);
		setInchesDisplay(parts.inches);
	}, [value]);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				width: "100%",
				minWidth: 0,
			}}
		>
			<TextField
				inputRef={feetRef}
				size="small"
				margin="none"
				value={feetDisplay}
				onChange={handleFeet}
				onBlur={onSegmentBlur}
				onKeyDown={onFeetKeyDown}
				label={label}
				InputLabelProps={label ? { shrink: true } : undefined}
				onFocus={(e) => {
					isEditingRef.current = true;
					e.target.select();
				}}
				placeholder="0"
				disabled={disabled}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
					"aria-label": "feet",
				}}
				InputProps={{
					endAdornment: <InputAdornment position="end">'</InputAdornment>,
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>

			<TextField
				inputRef={inchesRef}
				size="small"
				margin="none"
				value={inchesDisplay}
				onChange={handleInches}
				label={label ? "in" : undefined}
				InputLabelProps={label ? { shrink: true } : undefined}
				onBlur={onSegmentBlur}
				onFocus={(e) => {
					isEditingRef.current = true;
					e.target.select();
				}}
				placeholder="0"
				disabled={disabled}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
					"aria-label": "inches",
				}}
				InputProps={{
					endAdornment: <InputAdornment position="end">"</InputAdornment>,
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>
		</Box>
	);
};
